import { inject, Injectable } from '@angular/core';
import { Language } from '@api/src/models/UserService/Contract/language';
import { LoginService } from '@api/src/services';
import { ErrorResponse } from '@core/error/error-response.model';
import { AvailableLanguage } from '@core/localization/available.language';
import { LOCALIZATION_CONFIG, LocalizationConfig } from '@core/localization/localization.config';
import { StartWithUnit } from '@core/operators/start-with-unit';
import { StorageKeys } from '@core/storage/storage.keys';
import { TranslateService } from '@ngx-translate/core';
import { RxState } from '@rx-angular/state';
import { RxActionFactory, RxActions } from '@rx-angular/state/actions';
import { LocalStorageService } from 'ngx-webstorage';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

export type ILanguageState = {
    language: string;
    loading: Record<StartWithUnit, boolean>;
    errors: Record<StartWithUnit, ErrorResponse>;
    languages: Language[];
};

export type ILanguageAction = {
    requestLanguages: Language[];
    setCurrentLanguage: string;
};

@Injectable({
    providedIn: 'root',
})
export class LanguageState extends RxState<ILanguageState> {
    readonly #loginService: LoginService = inject(LoginService);
    readonly #translate: TranslateService = inject(TranslateService);
    readonly #localStorage: LocalStorageService = inject(LocalStorageService);
    readonly #actions: RxActions<ILanguageAction> = new RxActionFactory<ILanguageAction>().create();
    readonly #localizationConfig: LocalizationConfig = inject(LOCALIZATION_CONFIG);

    readonly #defaultLanguageCode: string = this.#localizationConfig.defaultLocale;

    public readonly loading$: Observable<Record<StartWithUnit, boolean>> = this.select('loading');
    public readonly language$: Observable<string> = this.select('language');
    public readonly languages$: Observable<Language[]> = this.select('languages');

    constructor() {
        super();

        this.setDefaultState();
        this.connectSelectors();

        this.#translate.setFallbackLang(AvailableLanguage.EnGb);
        this.setCurrentLanguage(this.#localStorage.retrieve(StorageKeys.Lang) || this.#defaultLanguageCode);
    }

    public get currentLanguage(): string {
        return this.#localStorage.retrieve(StorageKeys.Lang);
    }

    public setCurrentLanguage(languageCode: string): void {
        const isoCode: string = languageCode?.toLowerCase();

        if (isoCode === this.get('language')) {
            return;
        }

        this.#translate.use(isoCode);
        this.#actions.setCurrentLanguage(isoCode);
    }

    public requestLanguages(): void {
        this.hold(this.#loginService.apiLoginListLanguagesGet(), this.#actions.requestLanguages);
    }

    private setDefaultState(): void {
        this.set({
            loading: null,
            errors: null,
            languages: null,
        });
    }

    private connectSelectors(): void {
        this.connect(
            'language',
            this.#actions.setCurrentLanguage$.pipe(
                tap((language: string) => this.#localStorage.store(StorageKeys.Lang, language.toLowerCase())),
            ),
        );

        this.connect(
            'languages',
            this.#actions.requestLanguages$.pipe(
                tap((languages: Language[]) => {
                    const currentLanguage: string = this.#localStorage.retrieve(StorageKeys.Lang);

                    if (
                        languages.length > 0 &&
                        currentLanguage &&
                        !languages?.find((language: Language) => language?.lang.toLowerCase() === currentLanguage)
                    ) {
                        this.#localStorage.store(StorageKeys.Lang, languages[0].lang?.toLowerCase());
                    }
                }),
            ),
        );
    }
}
